
import React, { useState, useMemo, useEffect } from 'react';
import * as Firestore from 'firebase/firestore';
import { deleteField } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { User, Role, UserType, SubscriptionStatus, Subscription, Recipe, Household, Language, WeekStartDay } from '../types';
import { normalizeDietPreference, normalizeDietTypes } from '../utils/dietTypes';
import SearchField from './SearchField';
import ChevronDownIcon from './icons/ChevronDownIcon';
import SparklesIcon from './icons/SparklesIcon';
import { getTranslation } from '../utils/translations';
import ApproveSubscriptionModal from './ApproveSubscriptionModal';
import CopiedNoticeModal from './CopiedNoticeModal';
import InfoNoticeModal from './InfoNoticeModal';
import ConfirmationModal from './ConfirmationModal';
import MigrationsPanel from './MigrationsPanel';
import {
    buildLegacyRecipeOwnershipPatch,
    collectLegacyRecipeOwnershipCandidates,
    LEGACY_RECIPE_OWNERSHIP_MIGRATION,
} from '../utils/migrateLegacyRecipeOwnership';
import {
    buildChefLinksMigrationPatch,
    collectChefLinkMigrationCandidates,
} from '../utils/migrateChefLinks';
import {
    buildRecipeChefFromLinksPatch,
    collectRecipeChefMigrationCandidates,
} from '../utils/migrateRecipeChefFromLinks';
import {
    buildRecipeFilterMetadataPatch,
    collectRecipeFilterMetadataCandidates,
    summarizeRecipeFilterMetadataCandidates,
} from '../utils/migrateRecipeFilterMetadata';
import { getUserTypeLabel, isNutritionConsultant, normalizeUserType, USER_TYPES } from '../utils/userType';
import AvatarImage from './AvatarImage';
import { getInitials } from '../hooks/useConsultantBannerData';
import { DEFAULT_HOUSEHOLD_LIMIT, getUserHouseholdLimit } from '../utils/userHouseholdLimit';
import type { Chef } from '../types';

interface UserManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    users: User[];
    currentUser: User;
    onUpdateUserRole: (email: string, role: Role) => void;
    onUpdateUserType: (email: string, userType: UserType) => void;
    onUpdateHouseholdEntitlement?: (uid: string, entitlement: number) => void;
    onUpdateSubscriptionStatus?: (uid: string, status: SubscriptionStatus) => void;
    onUpdateAiEligibility: (uid: string, eligible: boolean) => void;
    onGenerateApiKey: (uid: string) => void;
    onImpersonateUser?: (user: User) => void;
    recipes: Recipe[];
    households: Household[];
}

const ROLES: Role[] = ['user', 'admin', 'superuser'];
type UserListFilter = Role | UserType | 'all';
type UserManagementView = 'users' | 'migrations';

const toDateInputValue = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

const fromDateInputValue = (value: string): string => {
    return new Date(`${value}T23:59:59.999`).toISOString();
};

const { writeBatch, doc, updateDoc, collection, getDocs, getDoc, query, where } = Firestore as any;

const ChartBarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
);

const InformationCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
);

const CopyIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
    </svg>
);

const EyeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const UserManagementModal: React.FC<UserManagementModalProps> = ({ 
    isOpen, 
    onClose, 
    users, 
    currentUser, 
    onUpdateUserRole,
    onUpdateUserType,
    onUpdateHouseholdEntitlement,
    onUpdateSubscriptionStatus,
    onUpdateAiEligibility,
    onGenerateApiKey,
    onImpersonateUser,
    recipes,
    households
}) => {
    const t = (key: any) => getTranslation(currentUser?.language || 'en', key);
    
    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<UserManagementView>('users');
    const [roleFilter, setRoleFilter] = useState<UserListFilter>('all');
    const [isMigrating, setIsMigrating] = useState(false);
    const [isPlanMigrating, setIsPlanMigrating] = useState(false);
    const [isUnitFixing, setIsUnitFixing] = useState(false);
    const [isDietMigrating, setIsDietMigrating] = useState(false);
    const [isLegacyRecipeMigrating, setIsLegacyRecipeMigrating] = useState(false);
    const [showCopiedNotice, setShowCopiedNotice] = useState(false);
    const [copiedNoticeKey, setCopiedNoticeKey] = useState<'copied' | 'idCopied' | 'textCopied'>('idCopied');
    const [legacyMigrationNotice, setLegacyMigrationNotice] = useState<{
        title: string;
        message?: string;
        variant: 'success' | 'info' | 'error';
    } | null>(null);
    const [legacyMigrationConfirmCount, setLegacyMigrationConfirmCount] = useState<number | null>(null);
    const [isChefLinksMigrating, setIsChefLinksMigrating] = useState(false);
    const [chefLinksMigrationConfirmCount, setChefLinksMigrationConfirmCount] = useState<number | null>(null);
    const [isRecipeChefMigrating, setIsRecipeChefMigrating] = useState(false);
    const [recipeChefMigrationConfirmCount, setRecipeChefMigrationConfirmCount] = useState<number | null>(null);
    const [isRecipeFilterMetadataMigrating, setIsRecipeFilterMetadataMigrating] = useState(false);
    const [recipeFilterMetadataConfirmCount, setRecipeFilterMetadataConfirmCount] = useState<number | null>(null);
    
    // Stats & Details Modal State
    const [statsUser, setStatsUser] = useState<User | null>(null);
    const [activeStatTab, setActiveStatTab] = useState<'summary' | 'recipes' | 'ownedHH' | 'memberHH'>('summary');
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [detailsUser, setDetailsUser] = useState<User | null>(null);
    const [detailsLanguage, setDetailsLanguage] = useState<Language>('en');
    const [detailsWeekStartDay, setDetailsWeekStartDay] = useState<WeekStartDay>('Monday');
    const [detailsUserType, setDetailsUserType] = useState<UserType>('regular');
    const [detailsHouseholdEntitlement, setDetailsHouseholdEntitlement] = useState(DEFAULT_HOUSEHOLD_LIMIT);
    const [detailsFreeForLife, setDetailsFreeForLife] = useState(false);
    const [detailsEndDate, setDetailsEndDate] = useState('');
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
    const [copiedApiKeyUid, setCopiedApiKeyUid] = useState<string | null>(null);
    const [cancelSubscriptionTarget, setCancelSubscriptionTarget] = useState<User | null>(null);
    const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
    const [activateSubscriptionTarget, setActivateSubscriptionTarget] = useState<User | null>(null);
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<'date_added' | 'name'>('date_added');

    const filteredUsers = useMemo(() => {
        let result = users || [];
        if (roleFilter !== 'all') {
            if (roleFilter === 'nutrition_consultant') {
                result = result.filter((u) => isNutritionConsultant(u));
            } else if (roleFilter === 'regular') {
                result = result.filter((u) => !isNutritionConsultant(u));
            } else {
                result = result.filter(u => u.role === roleFilter);
            }
        }
        if (!searchQuery.trim()) return result;
        const query = searchQuery.toLowerCase();
        return result.filter(user => {
            const emailMatch = (user.email || '').toLowerCase().includes(query);
            const nameMatch = (user.firstName && user.firstName.toLowerCase().includes(query)) ||
                              (user.lastName && user.lastName.toLowerCase().includes(query));
            const nickMatch = user.nickname && user.nickname.toLowerCase().includes(query);
            return emailMatch || nameMatch || nickMatch;
        });
    }, [users, searchQuery, roleFilter]);

    const sortedUsers = useMemo(() => {
        const result = [...filteredUsers];
        if (sortBy === 'date_added') {
            result.sort((a, b) => {
                const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
                const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
                const timeA = Number.isNaN(dateA) ? 0 : dateA;
                const timeB = Number.isNaN(dateB) ? 0 : dateB;
                return timeB - timeA; // newest first
            });
        } else if (sortBy === 'name') {
            result.sort((a, b) => {
                const displayNameA = a.nickname || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '';
                const displayNameB = b.nickname || `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email || '';
                return displayNameA.toLowerCase().localeCompare(displayNameB.toLowerCase());
            });
        }
        return result;
    }, [filteredUsers, sortBy]);

    useEffect(() => {
        if (!detailsUser) return;
        setDetailsLanguage(detailsUser.language || 'en');
        setDetailsWeekStartDay(detailsUser.weekStartDay || 'Monday');
        setDetailsUserType(normalizeUserType(detailsUser.userType));
        setDetailsHouseholdEntitlement(
            detailsUser.householdEntitlement ?? DEFAULT_HOUSEHOLD_LIMIT,
        );
        setDetailsFreeForLife(
            detailsUser.subscription?.status === 'subscribed' &&
            detailsUser.subscription?.plan === 'free_for_life'
        );
        setDetailsEndDate(toDateInputValue(detailsUser.subscription?.endDate));
    }, [detailsUser]);

    const getSubscriptionStatusText = (status?: SubscriptionStatus | string) => {
        if (!status) return '???';
        if (status === 'subscribed') return t('subscribed');
        if (status === 'pending') return t('pending');
        if (status === 'canceled') return t('canceled');
        if (status === 'declined') return t('declined');
        return status;
    };

    const formatSubscriptionDate = (iso: string, language?: Language) =>
        new Date(iso).toLocaleDateString(language || 'en');

    const formatConfirmMessage = (template: string, values: Record<string, string>) =>
        Object.entries(values).reduce(
            (message, [key, value]) => message.replace(new RegExp(`\\{${key}\\}`, 'g'), value),
            template
        );

    if (!isOpen) return null;

    const handleConfirmCancelSubscription = async () => {
        if (!cancelSubscriptionTarget) return;
        setIsCancelingSubscription(true);
        try {
            await handleUpdateSubscriptionStatus(cancelSubscriptionTarget.uid, 'canceled');
            setCancelSubscriptionTarget(null);
        } catch (error) {
            console.error('Failed to cancel subscription', error);
            alert(t('errorSaving'));
        } finally {
            setIsCancelingSubscription(false);
        }
    };

    const handleUpdateSubscriptionStatus = async (uid: string, status: SubscriptionStatus) => {
        const targetUser = users.find((u) => u.uid === uid);
        if (!targetUser?.subscription) return;
        const subscription: Subscription = {
            ...targetUser.subscription,
            status,
            endDate: status === 'canceled' ? new Date().toISOString() : targetUser.subscription.endDate,
            plan:
                status === 'canceled' && targetUser.subscription.plan === 'free_for_life'
                    ? 'free'
                    : targetUser.subscription.plan,
        };
        await updateDoc(doc(db, 'users', uid), { subscription });
    };

    const handleRoleChange = (email: string, newRole: string) => {
        if (ROLES.includes(newRole as Role)) {
            onUpdateUserRole(email, newRole as Role);
        }
    };

    const handleUserTypeChange = (email: string, newUserType: string) => {
        if (USER_TYPES.includes(newUserType as UserType)) {
            onUpdateUserType(email, newUserType as UserType);
        }
    };

    const handleHouseholdEntitlementBlur = (user: User, rawValue: string) => {
        if (!onUpdateHouseholdEntitlement || currentUser.role !== 'superuser') return;
        const parsed = Math.floor(Number(rawValue));
        if (!Number.isFinite(parsed) || parsed < 1) return;
        const current = user.householdEntitlement ?? DEFAULT_HOUSEHOLD_LIMIT;
        if (parsed === current) return;
        onUpdateHouseholdEntitlement(user.uid, parsed);
    };

    const handleCopyEmail = (email: string) => {
        navigator.clipboard.writeText(email);
        setCopiedEmail(email);
        setTimeout(() => setCopiedEmail(null), 2000);
    };

    const handleCopyApiKey = (uid: string, apiKey: string) => {
        navigator.clipboard.writeText(apiKey);
        setCopiedApiKeyUid(uid);
        setTimeout(() => setCopiedApiKeyUid(null), 2000);
    };

    const handleSaveUserPreferences = async () => {
        if (!detailsUser || currentUser.role !== 'superuser') return;
        setIsSavingDetails(true);
        try {
            const updates: Partial<User> = {};
            if (detailsPreferencesChanged) {
                updates.language = detailsLanguage;
                updates.weekStartDay = detailsWeekStartDay;
                updates.userType = detailsUserType;
                if (detailsUserType === 'nutrition_consultant') {
                    updates.householdEntitlement = Math.max(1, Math.floor(detailsHouseholdEntitlement));
                }
            }
            if (detailsSubscriptionChanged && detailsUser.subscription) {
                const baseSubscription = detailsUser.subscription;
                const subscription: Subscription = {
                    ...baseSubscription,
                    plan: detailsFreeForLife
                        ? 'free_for_life'
                        : baseSubscription.plan === 'free_for_life'
                            ? 'free'
                            : baseSubscription.plan,
                    endDate: detailsFreeForLife
                        ? null
                        : detailsEndDate
                            ? fromDateInputValue(detailsEndDate)
                            : null,
                };
                updates.subscription = subscription;
            }
            const firestoreUpdates: Record<string, unknown> = { ...updates };
            let clearHouseholdEntitlement = false;
            if (
                detailsPreferencesChanged &&
                detailsUserType !== 'nutrition_consultant' &&
                detailsUser.householdEntitlement !== undefined
            ) {
                firestoreUpdates.householdEntitlement = deleteField();
                clearHouseholdEntitlement = true;
            }
            if (Object.keys(firestoreUpdates).length === 0) return;
            await updateDoc(doc(db, 'users', detailsUser.uid), firestoreUpdates);
            const savedUser: User = {
                ...detailsUser,
                ...updates,
            };
            if (clearHouseholdEntitlement) {
                delete savedUser.householdEntitlement;
            }
            setDetailsUser(savedUser);
        } catch (error) {
            console.error('Failed to save user preferences', error);
            alert(t('errorSaving'));
        } finally {
            setIsSavingDetails(false);
        }
    };

    const detailsPreferencesChanged = detailsUser && (
        (detailsUser.language || 'en') !== detailsLanguage ||
        (detailsUser.weekStartDay || 'Monday') !== detailsWeekStartDay ||
        normalizeUserType(detailsUser.userType) !== detailsUserType ||
        (
            detailsUserType === 'nutrition_consultant' &&
            (detailsUser.householdEntitlement ?? DEFAULT_HOUSEHOLD_LIMIT) !== Math.max(1, Math.floor(detailsHouseholdEntitlement))
        )
    );

    const detailsSubscriptionChanged = !!detailsUser?.subscription && (
        (detailsUser.subscription.plan === 'free_for_life') !== detailsFreeForLife ||
        (!detailsFreeForLife && toDateInputValue(detailsUser.subscription.endDate) !== detailsEndDate)
    );

    const detailsHasChanges = !!(detailsPreferencesChanged || detailsSubscriptionChanged);

    const handleMigrateLegacyUsers = async () => {
        if (currentUser.role !== 'superuser') return;
        
        const usersToFix = users.filter(u => 
            !u.subscription || 
            u.subscription.plan === 'free' ||
            !u.creationDate || 
            !u.updateDate
        );
        
        if (usersToFix.length === 0) {
            alert(t('noResults'));
            return;
        }
        
        if (!window.confirm(`${t('migrationConfirm')} (${usersToFix.length} users)`)) return;
        setIsMigrating(true);
        try {
            const CHUNK_SIZE = 400;
            for (let i = 0; i < usersToFix.length; i += CHUNK_SIZE) {
                const chunk = usersToFix.slice(i, i + CHUNK_SIZE);
                const batch = writeBatch(db);
                chunk.forEach(u => {
                    const userRef = doc(db, 'users', u.uid);
                    const updates: any = {};
                    
                    if (!u.subscription || u.subscription.plan === 'free') {
                        updates.subscription = { 
                            status: u.subscription?.status || 'subscribed', 
                            startDate: u.subscription?.startDate || "2025-11-01", 
                            endDate: null, 
                            plan: 'free_for_life', 
                            planDetails: { price: 0, currency: 'USD', households: 5 }, 
                            adminViewed: true 
                        };
                    }
                    
                    if (!u.creationDate) updates.creationDate = "2025-11-01";
                    if (!u.updateDate) updates.updateDate = new Date().toISOString();
                    batch.update(userRef, updates);
                });
                await batch.commit();
            }
            alert(t('fixSuccess'));
        } catch (error) {
            alert("Error updating users.");
        } finally {
            setIsMigrating(false);
        }
    };

    const showCopiedMessage = (key: 'copied' | 'idCopied' | 'textCopied' = 'idCopied') => {
        setCopiedNoticeKey(key);
        setShowCopiedNotice(true);
    };

    const fetchLegacyRecipeMigrationCandidates = async (): Promise<Recipe[]> => {
        const householdSnap = await getDocs(
            query(
                collection(db, 'recipes'),
                where('householdId', '==', LEGACY_RECIPE_OWNERSHIP_MIGRATION.householdId),
            ),
        );
        const householdRecipes = householdSnap.docs.map(
            (recipeDoc: { id: string; data: () => Recipe }) => ({ ...recipeDoc.data(), id: recipeDoc.id } as Recipe),
        );

        const allRecipesSnap = await getDocs(collection(db, 'recipes'));
        const allRecipes = allRecipesSnap.docs.map(
            (recipeDoc: { id: string; data: () => Recipe }) => ({ ...recipeDoc.data(), id: recipeDoc.id } as Recipe),
        );

        return collectLegacyRecipeOwnershipCandidates([householdRecipes, allRecipes, recipes]);
    };

    const handleMigrateLegacyRecipeOwnership = async () => {
        if (currentUser.role !== 'superuser') return;

        setIsLegacyRecipeMigrating(true);
        try {
            const candidates = await fetchLegacyRecipeMigrationCandidates();

            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('noResults'),
                    message: t('migrateLegacyRecipeOwnershipNone'),
                    variant: 'info',
                });
                return;
            }

            setLegacyMigrationConfirmCount(candidates.length);
        } catch (error) {
            console.error('Legacy recipe ownership migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateLegacyRecipeOwnershipFailed'),
                variant: 'error',
            });
        } finally {
            setIsLegacyRecipeMigrating(false);
        }
    };

    const runLegacyRecipeOwnershipMigration = async () => {
        if (legacyMigrationConfirmCount == null) return;

        setLegacyMigrationConfirmCount(null);
        setIsLegacyRecipeMigrating(true);

        try {
            const candidates = await fetchLegacyRecipeMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('noResults'),
                    message: t('migrateLegacyRecipeOwnershipNone'),
                    variant: 'info',
                });
                return;
            }

            const CHUNK_SIZE = 400;
            let updated = 0;
            const patch = buildLegacyRecipeOwnershipPatch();

            for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = candidates.slice(i, i + CHUNK_SIZE);
                chunk.forEach((recipe) => {
                    batch.update(doc(db, 'recipes', recipe.id), patch);
                    updated++;
                });
                await batch.commit();
            }

            setLegacyMigrationNotice({
                title: t('migrateLegacyRecipeOwnershipDone').replace('{n}', String(updated)),
                variant: 'success',
            });
        } catch (error) {
            console.error('Legacy recipe ownership migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateLegacyRecipeOwnershipFailed'),
                variant: 'error',
            });
        } finally {
            setIsLegacyRecipeMigrating(false);
        }
    };

    const fetchChefLinkMigrationCandidates = async (): Promise<Chef[]> => {
        const snap = await getDocs(collection(db, 'chefs'));
        const chefs = snap.docs.map((chefDoc: { id: string; data: () => unknown }) => ({
            id: chefDoc.id,
            ...(chefDoc.data() as Omit<Chef, 'id'>),
        }));
        return collectChefLinkMigrationCandidates(chefs);
    };

    const handleMigrateChefLinks = async () => {
        if (currentUser.role !== 'superuser') return;
        setIsChefLinksMigrating(true);
        try {
            const candidates = await fetchChefLinkMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateChefLinksNone'),
                    variant: 'info',
                });
                return;
            }
            setChefLinksMigrationConfirmCount(candidates.length);
        } catch (error) {
            console.error('Chef links migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateChefLinksFailed'),
                variant: 'error',
            });
        } finally {
            setIsChefLinksMigrating(false);
        }
    };

    const runChefLinksMigration = async () => {
        if (chefLinksMigrationConfirmCount == null) return;
        setChefLinksMigrationConfirmCount(null);
        setIsChefLinksMigrating(true);
        try {
            const candidates = await fetchChefLinkMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateChefLinksNone'),
                    variant: 'info',
                });
                return;
            }

            const CHUNK_SIZE = 400;
            let updated = 0;
            for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = candidates.slice(i, i + CHUNK_SIZE);
                chunk.forEach((chef) => {
                    const patch = buildChefLinksMigrationPatch(chef);
                    if (!patch) return;
                    batch.update(doc(db, 'chefs', chef.id), patch);
                    updated++;
                });
                await batch.commit();
            }

            setLegacyMigrationNotice({
                title: t('migrateChefLinksDone').replace('{n}', String(updated)),
                variant: 'success',
            });
        } catch (error) {
            console.error('Chef links migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateChefLinksFailed'),
                variant: 'error',
            });
        } finally {
            setIsChefLinksMigrating(false);
        }
    };

    const fetchRecipeChefMigrationCandidates = async () => {
        const recipesSnap = await getDocs(collection(db, 'recipes'));
        const allRecipes = recipesSnap.docs.map(
            (recipeDoc: { id: string; data: () => Recipe }) => ({ ...recipeDoc.data(), id: recipeDoc.id } as Recipe),
        );
        const chefsSnap = await getDocs(collection(db, 'chefs'));
        const chefs = chefsSnap.docs.map((chefDoc: { id: string; data: () => unknown }) => ({
            id: chefDoc.id,
            ...(chefDoc.data() as Omit<Chef, 'id'>),
        }));
        return collectRecipeChefMigrationCandidates(allRecipes, chefs);
    };

    const handleMigrateRecipeChefFromLinks = async () => {
        if (currentUser.role !== 'superuser') return;
        setIsRecipeChefMigrating(true);
        try {
            const candidates = await fetchRecipeChefMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateRecipeChefFromLinksNone'),
                    variant: 'info',
                });
                return;
            }
            setRecipeChefMigrationConfirmCount(candidates.length);
        } catch (error) {
            console.error('Recipe chef association migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateRecipeChefFromLinksFailed'),
                variant: 'error',
            });
        } finally {
            setIsRecipeChefMigrating(false);
        }
    };

    const runRecipeChefFromLinksMigration = async () => {
        if (recipeChefMigrationConfirmCount == null) return;
        setRecipeChefMigrationConfirmCount(null);
        setIsRecipeChefMigrating(true);
        try {
            const candidates = await fetchRecipeChefMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateRecipeChefFromLinksNone'),
                    variant: 'info',
                });
                return;
            }

            const CHUNK_SIZE = 400;
            let updated = 0;
            for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = candidates.slice(i, i + CHUNK_SIZE);
                chunk.forEach(({ recipe, chefId }) => {
                    batch.update(doc(db, 'recipes', recipe.id), buildRecipeChefFromLinksPatch(chefId));
                    updated++;
                });
                await batch.commit();
            }

            setLegacyMigrationNotice({
                title: t('migrateRecipeChefFromLinksDone').replace('{n}', String(updated)),
                variant: 'success',
            });
        } catch (error) {
            console.error('Recipe chef association migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateRecipeChefFromLinksFailed'),
                variant: 'error',
            });
        } finally {
            setIsRecipeChefMigrating(false);
        }
    };

    const fetchRecipeFilterMetadataMigrationCandidates = async (): Promise<Recipe[]> => {
        const recipesSnap = await getDocs(collection(db, 'recipes'));
        const allRecipes = recipesSnap.docs.map((recipeDoc) => ({
            id: recipeDoc.id,
            ...(recipeDoc.data() as Omit<Recipe, 'id'>),
        }));
        return collectRecipeFilterMetadataCandidates(allRecipes);
    };

    const handleMigrateRecipeFilterMetadata = async () => {
        if (currentUser.role !== 'superuser') return;

        setIsRecipeFilterMetadataMigrating(true);
        try {
            const candidates = await fetchRecipeFilterMetadataMigrationCandidates();
            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateRecipeFilterMetadataNone'),
                    variant: 'info',
                });
                return;
            }
            setRecipeFilterMetadataConfirmCount(candidates.length);
        } catch (error) {
            console.error('Recipe filter metadata migration scan failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateRecipeFilterMetadataFailed'),
                variant: 'error',
            });
        } finally {
            setIsRecipeFilterMetadataMigrating(false);
        }
    };

    const runRecipeFilterMetadataMigration = async () => {
        if (recipeFilterMetadataConfirmCount == null) return;
        setRecipeFilterMetadataConfirmCount(null);
        setIsRecipeFilterMetadataMigrating(true);

        try {
            const recipesSnap = await getDocs(collection(db, 'recipes'));
            const allRecipes = recipesSnap.docs.map((recipeDoc) => ({
                id: recipeDoc.id,
                ...(recipeDoc.data() as Omit<Recipe, 'id'>),
            }));
            const summary = summarizeRecipeFilterMetadataCandidates(allRecipes);
            const candidates = collectRecipeFilterMetadataCandidates(allRecipes);

            if (candidates.length === 0) {
                setLegacyMigrationNotice({
                    title: t('migrateRecipeFilterMetadataNone'),
                    variant: 'info',
                });
                return;
            }

            const CHUNK_SIZE = 400;
            let updated = 0;
            for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = candidates.slice(i, i + CHUNK_SIZE);
                chunk.forEach((recipe) => {
                    const patch = buildRecipeFilterMetadataPatch(recipe);
                    if (!patch) return;

                    const updates: Record<string, unknown> = { ...patch.updates };
                    patch.deleteFields.forEach((field) => {
                        updates[field] = deleteField();
                    });
                    batch.update(doc(db, 'recipes', recipe.id), updates);
                    updated += 1;
                });
                await batch.commit();
            }

            const details = [
                `dietTypes: ${summary.fieldCounts.dietTypes}`,
                `isKeto removed: ${summary.fieldCounts.isKeto}`,
                `suitableFor: ${summary.fieldCounts.suitableFor}`,
                `lifestyleTags: ${summary.fieldCounts.lifestyleTags}`,
                `category: ${summary.fieldCounts.category}`,
                `prepTime: ${summary.fieldCounts.prepTime}`,
                `allergens: ${summary.fieldCounts.allergens}`,
            ].join('\n');

            setLegacyMigrationNotice({
                title: t('migrateRecipeFilterMetadataDone').replace('{n}', String(updated)),
                message: details,
                variant: 'success',
            });
        } catch (error) {
            console.error('Recipe filter metadata migration failed:', error);
            setLegacyMigrationNotice({
                title: t('migrateRecipeFilterMetadataFailed'),
                variant: 'error',
            });
        } finally {
            setIsRecipeFilterMetadataMigrating(false);
        }
    };

    const handleMigrateHouseholdPlans = async () => {
        if (currentUser.role !== 'superuser') return;
        if (!window.confirm(t('migrationConfirm'))) return;
        setIsPlanMigrating(true);
        try {
            const snap = await getDocs(collection(db, 'householdPlans'));
            const CHUNK_SIZE = 400;
            for (let i = 0; i < snap.docs.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = snap.docs.slice(i, i + CHUNK_SIZE);
                chunk.forEach((pDoc: any) => {
                    const id = pDoc.id;
                    const parts = id.split('_');
                    if (parts.length < 2) return;
                    const datePart = parts[parts.length - 1];
                    const hhIdPart = parts.slice(0, parts.length - 1).join('_');
                    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                    if (!dateRegex.test(datePart)) return;
                    const data = pDoc.data();
                    const updates: any = {};
                    if (!data.creationDate) updates.creationDate = datePart;
                    if (!data.weekStartDate) updates.weekStartDate = datePart;
                    if (!data.householdId) updates.householdId = hhIdPart;
                    if (Object.keys(updates).length > 0) batch.update(pDoc.ref, updates);
                });
                await batch.commit();
            }
            alert(t('fixSuccess'));
        } catch (error) {
            alert("Error updating household plans.");
        } finally {
            setIsPlanMigrating(false);
        }
    };

    const handleFixMyActiveCart = async () => {
        if (!currentUser.activeHouseholdId) return;
        setIsUnitFixing(true);
        try {
            const docRef = doc(db, 'activeShoppingCarts', currentUser.activeHouseholdId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.items && Array.isArray(data.items)) {
                    let changesCount = 0;
                    const newItems = data.items.map((item: any) => {
                        const originalUnit = item.unit || 'unit';
                        const unified = unifyUnit(originalUnit);
                        if (unified !== originalUnit) {
                            console.log(`[FixMyCart] Updating ${item.name}: ${originalUnit} -> ${unified}`);
                            changesCount++;
                            return { ...item, unit: unified };
                        }
                        return item;
                    });
                    
                    if (changesCount > 0) {
                        await updateDoc(docRef, { items: newItems });
                        alert(`Successfully fixed ${changesCount} items in your active cart!`);
                    } else {
                        alert("No items needed fixing in your active cart.");
                    }
                }
            } else {
                alert("Active cart not found.");
            }
        } catch (e) {
            console.error(e);
            alert("Error fixing active cart.");
        } finally {
            setIsUnitFixing(false);
        }
    };

    const handleMigrateDietTypes = async () => {
        if (currentUser.role !== 'superuser') return;
        if (!window.confirm('Migrate recipes to dietTypes and set user dietPreference to none where missing?')) return;

        setIsDietMigrating(true);
        try {
            const CHUNK_SIZE = 400;
            let recipesUpdated = 0;
            let usersUpdated = 0;

            const recipesSnap = await getDocs(collection(db, 'recipes'));
            for (let i = 0; i < recipesSnap.docs.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = recipesSnap.docs.slice(i, i + CHUNK_SIZE);
                chunk.forEach((recipeDoc: any) => {
                    const data = recipeDoc.data() as Recipe;
                    const needsDietTypes = !data.dietTypes;
                    const hasLegacyKeto = data.isKeto !== undefined;
                    if (!needsDietTypes && !hasLegacyKeto) return;

                    const updates: Record<string, unknown> = {
                        dietTypes: normalizeDietTypes(data.dietTypes, data.isKeto),
                    };
                    if (hasLegacyKeto) {
                        updates.isKeto = deleteField();
                    }
                    batch.update(recipeDoc.ref, updates);
                    recipesUpdated++;
                });
                await batch.commit();
            }

            for (let i = 0; i < users.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = users.slice(i, i + CHUNK_SIZE).filter((u) => !u.dietPreference);
                chunk.forEach((u) => {
                    batch.update(doc(db, 'users', u.uid), {
                        dietPreference: normalizeDietPreference(u.dietPreference),
                    });
                    usersUpdated++;
                });
                if (chunk.length > 0) {
                    await batch.commit();
                }
            }

            alert(`Diet migration complete. Recipes: ${recipesUpdated}, users: ${usersUpdated}.`);
        } catch (error) {
            console.error('Diet migration failed:', error);
            alert('Diet migration failed. Check console.');
        } finally {
            setIsDietMigrating(false);
        }
    };

    const handleFixRecipeUnits = async () => {
        if (currentUser.role !== 'superuser') return;
        if (!window.confirm("Normalize units? This will scan Recipes, Carts, and Active Carts.")) return;
        
        setIsUnitFixing(true);
        try {
            let totalUpdated = 0;
            const batchSize = 400;
            let batch = writeBatch(db);
            let opCount = 0;

            const commitBatch = async () => {
                if (opCount > 0) {
                    await batch.commit();
                    batch = writeBatch(db);
                    opCount = 0;
                }
            };

            console.log("Starting unit normalization...");

            // 1. Recipes
            const recipesSnap = await getDocs(collection(db, 'recipes'));
            for (const docSnap of recipesSnap.docs) {
                const data = docSnap.data() as Recipe;
                if (!data.ingredients) continue;
                let changed = false;
                const newIngs = data.ingredients.map(ing => {
                    const unified = unifyUnit(ing.unit || '');
                    if (unified !== ing.unit) {
                        changed = true;
                        return { ...ing, unit: unified };
                    }
                    return ing;
                });
                if (changed) {
                    batch.update(docSnap.ref, { ingredients: newIngs });
                    opCount++;
                    totalUpdated++;
                }
                if (opCount >= batchSize) await commitBatch();
            }

            // 2. Shopping Carts
            const cartsSnap = await getDocs(collection(db, 'shoppingCarts'));
            for (const docSnap of cartsSnap.docs) {
                const data = docSnap.data() as any; // ShoppingCart
                if (!data.ingredients) continue;
                let changed = false;
                const newIngs = data.ingredients.map((ing: any) => {
                    const unified = unifyUnit(ing.unit || '');
                    if (unified !== ing.unit) {
                        changed = true;
                        return { ...ing, unit: unified };
                    }
                    return ing;
                });
                if (changed) {
                    batch.update(docSnap.ref, { ingredients: newIngs });
                    opCount++;
                    totalUpdated++;
                }
                if (opCount >= batchSize) await commitBatch();
            }

            // 3. Active Shopping Carts
            const activeCartsSnap = await getDocs(collection(db, 'activeShoppingCarts'));
            for (const docSnap of activeCartsSnap.docs) {
                const data = docSnap.data() as any; // ActiveShoppingCart
                if (!data.items) continue;
                let changed = false;
                const newItems = data.items.map((item: any) => {
                    const unified = unifyUnit(item.unit || '');
                    if (unified !== item.unit) {
                        changed = true;
                        return { ...item, unit: unified };
                    }
                    return item;
                });
                if (changed) {
                    batch.update(docSnap.ref, { items: newItems });
                    opCount++;
                    totalUpdated++;
                }
                if (opCount >= batchSize) await commitBatch();
            }

            await commitBatch();
            console.log(`Normalization complete. Updated ${totalUpdated} documents.`);
            alert(`Normalization complete. Updated ${totalUpdated} documents. Check console for details.`);
        } catch (error) {
            console.error("Error fixing units:", error);
            alert("Error fixing units. Check console.");
        } finally {
            setIsUnitFixing(false);
        }
    };

    const handleInitSingleSub = async (uid: string) => {
        const userRef = doc(db, 'users', uid);
        const now = new Date().toISOString();
        const updates = { 
            subscription: { 
                status: 'subscribed', 
                startDate: now, 
                endDate: null, 
                plan: 'free_for_life', 
                planDetails: { price: 0, currency: 'USD', households: 5 }, 
                adminViewed: true 
            }, 
            creationDate: now, 
            updateDate: now 
        };
        try { await updateDoc(userRef, updates); alert(t('fixSuccess')); } catch (e) { alert("Failed."); }
    };

    const getUserStats = (user: User) => {
        const ownedRecipesList = recipes.filter(r => r.ownerId === user.uid && !r.isDeleted);
        const ownedHouseholdsList = households.filter(h => h.ownerId === user.uid);
        const memberHouseholdsList = households.filter(h => h.members.includes(user.uid) && h.ownerId !== user.uid);
        return { 
            ownedRecipes: ownedRecipesList.length, 
            ownedRecipesList,
            ownedHouseholds: ownedHouseholdsList.length, 
            ownedHouseholdsList,
            memberHouseholds: memberHouseholdsList.length,
            memberHouseholdsList 
        };
    };
    
    return (
        <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transition-colors" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('systemUserManagement')}</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{users.length} {t('totalUsers')}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors p-2">&times;</button>
                </div>

                <div className="p-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 space-y-4">
                    <div className="space-y-3">
                        <SearchField
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder={t('searchUsers')}
                            clearLabel={t('clearSearch')}
                            inputClassName="py-2 rounded-xl focus:ring-sky-500"
                        />
                        {currentUser.role === 'superuser' && (
                            <div className="flex rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-50 dark:bg-slate-800 p-1 gap-1">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('users')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-black uppercase tracking-wider transition-colors ${
                                        viewMode === 'users'
                                            ? 'bg-white dark:bg-slate-900 text-sky-600 dark:text-sky-400 shadow-sm'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {t('users')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('migrations')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5 ${
                                        viewMode === 'migrations'
                                            ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                    }`}
                                >
                                    <SparklesIcon className="w-3.5 h-3.5" />
                                    {t('migrations')}
                                </button>
                            </div>
                        )}
                        <div className={`flex gap-3 ${viewMode === 'migrations' ? 'hidden' : ''}`}>
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value as UserListFilter)}
                                className="flex-1 p-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-sky-500 min-w-0 font-bold text-slate-600 dark:text-slate-300"
                            >
                                <option value="all">{t('allRoles')}</option>
                                <option value="superuser">{t('superusers')}</option>
                                <option value="admin">{t('admins')}</option>
                                <option value="user">{t('users')}</option>
                                <option value="nutrition_consultant">{t('userTypeNutritionConsultant')}</option>
                            </select>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className="flex-1 p-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm outline-none focus:ring-2 focus:ring-sky-500 min-w-0 font-bold text-slate-600 dark:text-slate-300"
                            >
                                <option value="date_added">{t('sortByDateAdded')}</option>
                                <option value="name">{t('sortByName')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto bg-slate-50/50 dark:bg-slate-800/30 flex-grow space-y-4">
                    {viewMode === 'migrations' ? (
                        <MigrationsPanel title={t('migrationsPanelTitle')} defaultOpen={true}>
                            <button onClick={handleMigrateChefLinks} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-brand-sage/10 dark:bg-brand-sage/20 text-brand-sage px-4 py-2 rounded-lg border border-brand-sage/20 hover:bg-brand-sage/15 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isChefLinksMigrating ? '...' : t('migrateChefLinks')}
                            </button>
                            <button onClick={handleMigrateRecipeChefFromLinks} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-4 py-2 rounded-lg border border-teal-100 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isRecipeChefMigrating ? '...' : t('migrateRecipeChefFromLinks')}
                            </button>
                            <button onClick={handleMigrateLegacyRecipeOwnership} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-4 py-2 rounded-lg border border-orange-100 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isLegacyRecipeMigrating ? '...' : t('migrateLegacyRecipeOwnership')}
                            </button>
                            <button onClick={handleMigrateHouseholdPlans} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 px-4 py-2 rounded-lg border border-sky-100 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isPlanMigrating ? '...' : t('fixPlanMetadata')}
                            </button>
                            <button onClick={handleMigrateLegacyUsers} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-4 py-2 rounded-lg border border-sky-100 dark:border-amber-800 hover:bg-sky-100 dark:hover:bg-amber-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isMigrating ? '...' : "MIGRATE TO FREE FOR LIFE"}
                            </button>
                            <button onClick={handleFixRecipeUnits} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-lg border border-purple-100 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isUnitFixing ? '...' : "Fix Recipe Units (Global)"}
                            </button>
                            <button onClick={handleMigrateDietTypes} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-4 py-2 rounded-lg border border-emerald-100 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isDietMigrating ? '...' : 'Migrate Diet Types'}
                            </button>
                            <button onClick={handleMigrateRecipeFilterMetadata} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-4 py-2 rounded-lg border border-indigo-100 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all">
                                <SparklesIcon className="w-3.5 h-3.5" />
                                {isRecipeFilterMetadataMigrating ? '...' : t('migrateRecipeFilterMetadata')}
                            </button>
                            {(currentUser.role === 'superuser' || currentUser.role === 'admin') && (
                                <button onClick={handleFixMyActiveCart} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-lg border border-green-100 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all">
                                    <SparklesIcon className="w-3.5 h-3.5" />
                                    Fix My Active Cart
                                </button>
                            )}
                        </MigrationsPanel>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                            <p className="text-slate-400 dark:text-slate-500 font-bold">{t('noResults')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {sortedUsers.map(user => {
                                const isSelf = user.email === currentUser.email;
                                const isThisSuperuser = user.role === 'superuser';
                                const isDisabled = isSelf || (isThisSuperuser && currentUser.email !== user.email);
                                const namePart = user.nickname || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                const hasName = !!namePart;
                                const titleText = hasName ? namePart : user.email;
                                const avatarInitials = getInitials(titleText);
                                const isSubscribed = user.subscription?.status === 'subscribed';
                                const isCanceled = user.subscription?.status === 'canceled';
                                const isFreeForLife = isSubscribed && user.subscription?.plan === 'free_for_life';
                                const isCopied = copiedEmail === user.email;
                                const isApiKeyCopied = copiedApiKeyUid === user.uid;
                                const isExpanded = expandedUserId === user.uid;

                                return (
                                    <div key={user.uid} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden relative hover:border-sky-200 dark:hover:border-sky-800 transition-colors group">
                                        <div className="p-5 flex flex-col gap-3">
                                            <div className="flex justify-between items-start gap-2">
                                                <div
                                                    onClick={() => setExpandedUserId(isExpanded ? null : user.uid)}
                                                    className="flex-grow min-w-0 text-start flex items-start gap-3 cursor-pointer"
                                                >
                                                    <AvatarImage
                                                        src={user.avatarUrl}
                                                        alt={titleText}
                                                        className="w-11 h-11 rounded-full object-cover border-2 border-white dark:border-slate-900 shadow-sm bg-brand-sage/15 flex-shrink-0"
                                                        fallback={
                                                            <div className="w-11 h-11 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center font-black text-sm flex-shrink-0 border-2 border-white dark:border-slate-900">
                                                                {avatarInitials}
                                                            </div>
                                                        }
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="font-black text-slate-800 dark:text-slate-100 group-hover:text-sky-700 dark:group-hover:text-sky-400 transition-colors truncate">{titleText}</h4>
                                                            {isSelf && <span className="text-[10px] bg-sky-600 dark:bg-sky-500 text-white px-1.5 py-0.5 rounded font-black uppercase tracking-tighter flex-shrink-0">{t('me')}</span>}
                                                            {isNutritionConsultant(user) && (
                                                                <span className="text-[10px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter flex-shrink-0">
                                                                    {t('userTypeNutritionConsultant')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {hasName && (
                                                            <div className="flex items-center gap-1.5 group/email">
                                                                <p className="text-sm text-slate-400 dark:text-slate-500 font-medium dir-ltr text-start truncate max-w-[200px]">{user.email}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 flex-shrink-0 items-start">
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedUserId(isExpanded ? null : user.uid)}
                                                        className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg transition-all"
                                                    >
                                                        <ChevronDownIcon className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                    </button>
                                                </div>
                                            </div>

                                            {!isExpanded && (
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleCopyEmail(user.email); }}
                                                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isCopied ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-sky-600'}`}
                                                    >
                                                        {isCopied ? t('copied') : t('copy')}
                                                    </button>
                                                    {user.subscription && (
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${isSubscribed ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : isCanceled ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                                            {t('subscription')}: {getSubscriptionStatusText(user.subscription.status)}
                                                        </span>
                                                    )}
                                                    {isFreeForLife && (
                                                        <span className="text-[10px] px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-full font-bold uppercase">
                                                            {t('plan_free_for_life')}
                                                        </span>
                                                    )}
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${user.isAiEligible ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                                                        {user.isAiEligible ? t('aiEnabled') : t('aiDisabled')}
                                                    </span>
                                                </div>
                                            )}

                                            {isExpanded && (
                                                <>
                                        <div className="flex items-center gap-1.5 group/email border-t border-slate-50 dark:border-slate-800 pt-3">
                                            <p className="text-sm text-slate-400 dark:text-slate-500 font-medium dir-ltr text-start truncate">{user.email}</p>
                                            <button 
                                                onClick={() => handleCopyEmail(user.email)}
                                                className={`p-1 rounded-md transition-all ${isCopied ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'text-slate-300 dark:text-slate-600 hover:text-sky-500 hover:bg-sky-50 dark:hover:bg-sky-900/20'}`}
                                                title={isCopied ? t('copied') : t('copy')}
                                            >
                                                {isCopied ? (
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                                ) : (
                                                    <CopyIcon className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-2 border-t border-slate-50 dark:border-slate-800 pt-3">
                                            {currentUser.role === 'superuser' && onImpersonateUser && !isSelf && !isThisSuperuser && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onImpersonateUser(user); }}
                                                    className="p-2 text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/35 rounded-xl transition-all"
                                                    title={t('viewAsUser')}
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setDetailsUser(user); }}
                                                className="p-2 text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/35 rounded-xl transition-all"
                                                title={t('userDetails')}
                                            >
                                                <InformationCircleIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setStatsUser(user); setActiveStatTab('summary'); setExpandedItemId(null); }}
                                                className="p-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/35 rounded-xl transition-all"
                                                title={t('usageStatistics')}
                                            >
                                                <ChartBarIcon className="w-5 h-5" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-50 dark:border-slate-800 pt-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('systemRole')}</label>
                                                <select
                                                    value={user.role}
                                                    onChange={(e) => handleRoleChange(user.email, e.target.value)}
                                                    disabled={isDisabled}
                                                    className="w-full p-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-xs font-bold disabled:opacity-50 text-slate-700 dark:text-slate-300"
                                                >
                                                    {currentUser.role === 'superuser' && <option value="superuser">{t('superusers')}</option>}
                                                    <option value="admin">{t('admins')}</option>
                                                    <option value="user">{t('users')}</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('userType')}</label>
                                                <select
                                                    value={normalizeUserType(user.userType)}
                                                    onChange={(e) => handleUserTypeChange(user.email, e.target.value)}
                                                    disabled={isDisabled}
                                                    className="w-full p-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-xs font-bold disabled:opacity-50 text-slate-700 dark:text-slate-300"
                                                >
                                                    {USER_TYPES.map((type) => (
                                                        <option key={type} value={type}>{getUserTypeLabel(currentUser.language, type)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('subscription')}</label>
                                                {!user.subscription ? (
                                                    <button onClick={() => handleInitSingleSub(user.uid)} className="w-full p-1.5 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-100 dark:border-sky-800 rounded-lg text-xs font-black">{t('initialize')}</button>
                                                ) : (
                                                    currentUser.role === 'superuser' && !isSelf ? (
                                                        isSubscribed ? (
                                                            <button 
                                                                onClick={() => setCancelSubscriptionTarget(user)}
                                                                className="w-full p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900 rounded-lg text-xs font-black hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                                                            >
                                                                {t('cancelPlan')}
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                onClick={() => setActivateSubscriptionTarget(user)}
                                                                className="w-full p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-100 dark:border-green-900 rounded-lg text-xs font-black hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                                                            >
                                                                {t('activate')}
                                                            </button>
                                                        )
                                                    ) : (
                                                        <div className={`p-1.5 rounded-lg text-xs font-black text-center ${isSubscribed ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                                                            {isFreeForLife ? t('plan_free_for_life') : getSubscriptionStatusText(user.subscription.status)}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {isNutritionConsultant(user) && currentUser.role === 'superuser' && (
                                            <div className="border-t border-slate-50 dark:border-slate-800 pt-4">
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">
                                                    {t('householdEntitlement')}
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        defaultValue={user.householdEntitlement ?? DEFAULT_HOUSEHOLD_LIMIT}
                                                        key={`${user.uid}-${user.householdEntitlement ?? 'default'}`}
                                                        disabled={isDisabled}
                                                        onBlur={(e) => handleHouseholdEntitlementBlur(user, e.target.value)}
                                                        className="w-full p-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-xs font-bold disabled:opacity-50 text-slate-700 dark:text-slate-300"
                                                    />
                                                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 whitespace-nowrap">
                                                        {households.filter((h) => h.ownerId === user.uid).length}/{getUserHouseholdLimit(user)}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{t('householdEntitlementHint')}</p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-3 mt-4 border-t border-slate-50 dark:border-slate-800 pt-4">
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('aiEligible')}</label>
                                                <button 
                                                    onClick={() => onUpdateAiEligibility(user.uid, !user.isAiEligible)}
                                                    className={`w-full p-1.5 rounded-lg text-xs font-black border transition-colors ${user.isAiEligible ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 border-purple-100 dark:border-purple-900' : 'bg-slate-50 dark:bg-slate-800 text-slate-400 border-slate-100 dark:border-slate-800'}`}
                                                >
                                                    {user.isAiEligible ? t('aiEnabled').toUpperCase() : t('aiDisabled').toUpperCase()}
                                                </button>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">{t('apiKey')}</label>
                                                <div className="flex gap-1.5">
                                                    <div className="flex-grow min-w-0 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-lg p-1.5 text-[9px] font-mono text-slate-500 truncate">
                                                        {user.apiKey || 'NONE'}
                                                    </div>
                                                    {user.apiKey && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopyApiKey(user.uid, user.apiKey!)}
                                                            className={`p-1.5 border rounded-lg transition-colors ${
                                                                isApiKeyCopied
                                                                    ? 'bg-green-100 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                                                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-100 dark:border-slate-800 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20'
                                                            }`}
                                                            title={isApiKeyCopied ? t('copied') : t('copy')}
                                                        >
                                                            {isApiKeyCopied ? (
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                                            ) : (
                                                                <CopyIcon className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    )}
                                                    <button 
                                                        type="button"
                                                        onClick={() => onGenerateApiKey(user.uid)}
                                                        className="p-1.5 bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 border border-sky-100 dark:border-sky-900 rounded-lg hover:bg-sky-100 transition-colors"
                                                        title={t('generateKey')}
                                                    >
                                                        <SparklesIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 pt-2">
                                            {user.subscription && (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${isSubscribed ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : isCanceled ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                                    {t('subscription')}: {getSubscriptionStatusText(user.subscription.status)}
                                                </span>
                                            )}
                                            {isFreeForLife && (
                                                <span className="text-[10px] px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-full font-bold uppercase">
                                                    {t('plan_free_for_life')}
                                                </span>
                                            )}
                                            {user.subscription?.endDate && !isFreeForLife && (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${isCanceled ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                                                    {t('subscriptionEnds')}: {formatSubscriptionDate(user.subscription.endDate, currentUser.language)}
                                                </span>
                                            )}
                                            {user.creationDate && (
                                                <span className="text-[10px] px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 rounded-full font-bold uppercase">
                                                    {t('joined')}: {formatSubscriptionDate(user.creationDate, currentUser.language)}
                                                </span>
                                            )}
                                        </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                 <div className="p-4 bg-slate-100 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end">
                    <button onClick={onClose} className="px-8 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all shadow-lg">{t('close')}</button>
                </div>
            </div>

            {/* Statistics Overlay */}
            {statsUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setStatsUser(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="font-black text-slate-800 dark:text-slate-100 flex items-center gap-2 uppercase tracking-tight">
                                <ChartBarIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                                {activeStatTab === 'summary' ? t('statistics') : activeStatTab === 'recipes' ? t('userRecipes') : t('userHouseholds')}
                            </h3>
                            <button onClick={() => setStatsUser(null)} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-xl font-bold">&times;</button>
                        </div>
                        <div className="p-6">
                            <p className="text-xs font-bold text-slate-400 dark:text-slate-500 mb-6 uppercase tracking-widest text-center truncate">{statsUser.nickname || statsUser.email}</p>
                            
                            {activeStatTab === 'summary' ? (
                                <div className="space-y-3">
                                    {(() => {
                                        const stats = getUserStats(statsUser);
                                        return (
                                            <>
                                                <button 
                                                    onClick={() => { setActiveStatTab('recipes'); setExpandedItemId(null); }}
                                                    className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-sky-300 dark:hover:border-sky-700 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-all text-left"
                                                >
                                                    <span className="text-slate-600 dark:text-slate-300 font-bold text-sm">{t('recipesOwned')}</span>
                                                    <span className="text-2xl font-black text-sky-700 dark:text-sky-400">{stats.ownedRecipes}</span>
                                                </button>
                                                <button 
                                                    onClick={() => { setActiveStatTab('ownedHH'); setExpandedItemId(null); }}
                                                    className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-amber-300 dark:hover:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all text-left"
                                                >
                                                    <span className="text-slate-600 dark:text-slate-300 font-bold text-sm">{t('householdsOwned')}</span>
                                                    <span className="text-2xl font-black text-amber-700 dark:text-amber-400">{stats.ownedHouseholds}</span>
                                                </button>
                                                <button 
                                                    onClick={() => { setActiveStatTab('memberHH'); setExpandedItemId(null); }}
                                                    className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all text-left"
                                                >
                                                    <span className="text-slate-600 dark:text-slate-300 font-bold text-sm">{t('otherMemberships')}</span>
                                                    <span className="text-2xl font-black text-green-700 dark:text-green-400">{stats.memberHouseholds}</span>
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="max-h-[300px] overflow-y-auto pr-2 space-y-2 scrollbar-none">
                                        {(() => {
                                            const stats = getUserStats(statsUser);
                                            const list = activeStatTab === 'recipes' ? stats.ownedRecipesList 
                                                        : activeStatTab === 'ownedHH' ? stats.ownedHouseholdsList 
                                                        : stats.memberHouseholdsList;
                                            
                                            if (list.length === 0) return <p className="text-center py-10 text-slate-400 dark:text-slate-500 text-sm font-bold">{t('noItemsFound')}</p>;

                                            return list.map((item: any, idx: number) => {
                                                const itId = item.id;
                                                const isEx = expandedItemId === itId;
                                                return (
                                                    <div key={itId || idx} className="bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
                                                        <div className="p-3 flex justify-between items-center">
                                                            <div className="flex items-center gap-2 overflow-hidden">
                                                                <span className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">{item.name || t('unnamedItem')}</span>
                                                                {activeStatTab === 'recipes' && item.isPublic && <span className="text-[8px] bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1 py-0.5 rounded font-black flex-shrink-0">PUBLIC</span>}
                                                            </div>
                                                            <button 
                                                                onClick={() => setExpandedItemId(isEx ? null : itId)}
                                                                className={`p-1.5 rounded-lg transition-colors ${isEx ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' : 'text-slate-400 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300'}`}
                                                            >
                                                                <InformationCircleIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                        {isEx && (
                                                            <div className="px-3 pb-3 pt-1 border-t border-slate-200/50 dark:border-slate-700/50 bg-white/50 dark:bg-slate-900/50 animate-fade-in">
                                                                <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                                                    <code className="text-[10px] font-mono text-slate-500 dark:text-slate-400 break-all select-all flex-grow">{itId}</code>
                                                                    <button 
                                                                        onClick={() => { navigator.clipboard.writeText(itId); showCopiedMessage('idCopied'); }}
                                                                        className="text-[9px] font-black text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm"
                                                                    >
                                                                        {t('copy').toUpperCase()}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                    <button 
                                        onClick={() => { setActiveStatTab('summary'); setExpandedItemId(null); }}
                                        className="w-full py-2 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        &larr; {t('backToSummary')}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-center">
                            <button onClick={() => setStatsUser(null)} className="px-10 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all">{t('save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details & Preferences Overlay */}
            {detailsUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setDetailsUser(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100 dark:border-slate-800" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="font-black text-slate-800 dark:text-slate-100 flex items-center gap-2 uppercase tracking-tight">
                                <InformationCircleIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                                {t('userDetails')}
                            </h3>
                            <button onClick={() => setDetailsUser(null)} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-xl font-bold">&times;</button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('userId')}</label>
                                    <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                        <code className="text-xs text-slate-600 dark:text-slate-300 font-mono break-all select-all flex-grow">{detailsUser.uid}</code>
                                        <button onClick={() => { navigator.clipboard.writeText(detailsUser.uid); showCopiedMessage('idCopied'); }} className="text-[10px] font-black text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 px-2 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded shadow-sm">{t('copy').toUpperCase()}</button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('language')}</label>
                                        {currentUser.role === 'superuser' ? (
                                            <select
                                                value={detailsLanguage}
                                                onChange={(e) => setDetailsLanguage(e.target.value as Language)}
                                                className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-sky-500"
                                            >
                                                <option value="en">English</option>
                                                <option value="he">עברית</option>
                                                <option value="ar">العربية</option>
                                                <option value="fr">Français</option>
                                                <option value="de">Deutsch</option>
                                                <option value="es">Español</option>
                                            </select>
                                        ) : (
                                            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 capitalize">
                                                {detailsUser.language || 'Default (EN)'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('weekStart')}</label>
                                        {currentUser.role === 'superuser' ? (
                                            <select
                                                value={detailsWeekStartDay}
                                                onChange={(e) => setDetailsWeekStartDay(e.target.value as WeekStartDay)}
                                                className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-sky-500"
                                            >
                                                <option value="Monday">{t('startMonday')}</option>
                                                <option value="Sunday">{t('startSunday')}</option>
                                            </select>
                                        ) : (
                                            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300">
                                                {detailsUser.weekStartDay || 'Monday'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('userType')}</label>
                                    {currentUser.role === 'superuser' ? (
                                        <select
                                            value={detailsUserType}
                                            onChange={(e) => setDetailsUserType(e.target.value as UserType)}
                                            className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-sky-500"
                                        >
                                            {USER_TYPES.map((type) => (
                                                <option key={type} value={type}>{getUserTypeLabel(currentUser.language, type)}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300">
                                            {getUserTypeLabel(currentUser.language, normalizeUserType(detailsUser.userType))}
                                        </div>
                                    )}
                                </div>
                                {detailsUserType === 'nutrition_consultant' && currentUser.role === 'superuser' && (
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('householdEntitlement')}</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={detailsHouseholdEntitlement}
                                            onChange={(e) => setDetailsHouseholdEntitlement(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                                            className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                        <p className="text-xs text-slate-400 dark:text-slate-500">{t('householdEntitlementHint')}</p>
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('termsAccepted')}</label>
                                    <div className={`p-2 rounded-lg border text-sm font-bold ${detailsUser.acceptedTerms ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800 text-red-700 dark:text-red-400'}`}>
                                        {detailsUser.acceptedTerms ? t('yes') : t('no')}
                                    </div>
                                </div>
                                {detailsUser.subscription && (
                                    <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('subscription')}</label>
                                            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300">
                                                {getSubscriptionStatusText(detailsUser.subscription.status)}
                                            </div>
                                        </div>
                                        {currentUser.role === 'superuser' ? (
                                            <>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={detailsFreeForLife}
                                                        onChange={(e) => {
                                                            setDetailsFreeForLife(e.target.checked);
                                                            if (e.target.checked) setDetailsEndDate('');
                                                        }}
                                                        className="rounded border-slate-300 text-brand-sage focus:ring-brand-sage"
                                                    />
                                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{t('plan_free_for_life')}</span>
                                                </label>
                                                {!detailsFreeForLife && (
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('subscriptionEnds')}</label>
                                                        <input
                                                            type="date"
                                                            value={detailsEndDate}
                                                            onChange={(e) => setDetailsEndDate(e.target.value)}
                                                            className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-bold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-sky-500"
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="space-y-2">
                                                {detailsUser.subscription.status === 'subscribed' && detailsUser.subscription.plan === 'free_for_life' ? (
                                                    <div className="p-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800 rounded-lg text-sm font-bold text-sky-700 dark:text-sky-300">
                                                        {t('plan_free_for_life')}
                                                    </div>
                                                ) : detailsUser.subscription.endDate && detailsUser.subscription.status === 'subscribed' ? (
                                                    <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg text-sm font-bold text-amber-700 dark:text-amber-300">
                                                        {t('subscriptionEnds')}: {formatSubscriptionDate(detailsUser.subscription.endDate, currentUser.language)}
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-center gap-2">
                            {currentUser.role === 'superuser' && (
                                <button
                                    type="button"
                                    onClick={() => void handleSaveUserPreferences()}
                                    disabled={!detailsHasChanges || isSavingDetails}
                                    className="px-8 py-2.5 bg-brand-sage text-white font-black rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                                >
                                    {isSavingDetails ? '...' : t('save')}
                                </button>
                            )}
                            <button onClick={() => setDetailsUser(null)} className="px-10 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all">{t('close')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {cancelSubscriptionTarget && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
                <div
                    className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl p-6 border dark:border-slate-800"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                    </div>
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 mb-2 text-center">{t('cancelPlan')}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 text-center leading-relaxed">
                        {formatConfirmMessage(t('confirmCancelPlan'), { email: cancelSubscriptionTarget.email })}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-6 text-center">{t('confirmCancelPlanHint')}</p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setCancelSubscriptionTarget(null)}
                            disabled={isCancelingSubscription}
                            className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black rounded-xl text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleConfirmCancelSubscription()}
                            disabled={isCancelingSubscription}
                            className="flex-1 py-3 bg-red-600 text-white font-black rounded-xl text-xs uppercase tracking-widest hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                            {isCancelingSubscription ? '...' : t('cancelPlan')}
                        </button>
                    </div>
                </div>
            </div>
        )}
        <ApproveSubscriptionModal
            user={activateSubscriptionTarget}
            language={currentUser.language}
            onClose={() => setActivateSubscriptionTarget(null)}
        />
        <CopiedNoticeModal
            isOpen={showCopiedNotice}
            onClose={() => setShowCopiedNotice(false)}
            messageKey={copiedNoticeKey}
            language={currentUser.language}
        />
        <InfoNoticeModal
            isOpen={legacyMigrationNotice != null}
            onClose={() => setLegacyMigrationNotice(null)}
            title={legacyMigrationNotice?.title ?? ''}
            message={legacyMigrationNotice?.message}
            variant={legacyMigrationNotice?.variant ?? 'info'}
            language={currentUser.language}
        />
        <ConfirmationModal
            isOpen={legacyMigrationConfirmCount != null}
            title={t('migrationConfirm')}
            message={`${t('migrateLegacyRecipeOwnershipConfirm')} (${legacyMigrationConfirmCount ?? 0})`}
            confirmText={t('ok')}
            cancelText={t('cancel')}
            type="warning"
            onConfirm={runLegacyRecipeOwnershipMigration}
            onCancel={() => setLegacyMigrationConfirmCount(null)}
        />
        <ConfirmationModal
            isOpen={chefLinksMigrationConfirmCount != null}
            title={t('migrationConfirm')}
            message={`${t('migrateChefLinksConfirm')} (${chefLinksMigrationConfirmCount ?? 0})`}
            confirmText={t('ok')}
            cancelText={t('cancel')}
            type="warning"
            onConfirm={runChefLinksMigration}
            onCancel={() => setChefLinksMigrationConfirmCount(null)}
        />
        <ConfirmationModal
            isOpen={recipeChefMigrationConfirmCount != null}
            title={t('migrationConfirm')}
            message={`${t('migrateRecipeChefFromLinksConfirm')} (${recipeChefMigrationConfirmCount ?? 0})`}
            confirmText={t('ok')}
            cancelText={t('cancel')}
            type="warning"
            onConfirm={runRecipeChefFromLinksMigration}
            onCancel={() => setRecipeChefMigrationConfirmCount(null)}
        />
        <ConfirmationModal
            isOpen={recipeFilterMetadataConfirmCount != null}
            title={t('migrationConfirm')}
            message={`${t('migrateRecipeFilterMetadataConfirm')} (${recipeFilterMetadataConfirmCount ?? 0})`}
            confirmText={t('ok')}
            cancelText={t('cancel')}
            type="warning"
            onConfirm={runRecipeFilterMetadataMigration}
            onCancel={() => setRecipeFilterMetadataConfirmCount(null)}
        />
        </>
    );
};

export default UserManagementModal;
